/* eslint-disable no-param-reassign */
import type { PayloadAction, ThunkAction } from '@reduxjs/toolkit';
import { addBreadcrumb } from '@sentry/react';
import { createSlice } from '@reduxjs/toolkit';
import _ from 'lodash';
import type { Socket } from 'phoenix';
import type { Action } from 'redux';
import { purgeApolloCache } from '../app/apollo/cache';
import type { RootState } from '../app/redux/getReduxStore';
import type {
  CurrentSessionFragment,
  Organization,
  UserSettings,
  VisitorSession,
} from '../graphql/generated/graphql';
import {
  AuthenticationMode,
  BillingSummaryStatus,
  PlanTier,
  UserType,
} from '../graphql/generated/graphql';
import Logger from '../utils/Logger';
import SocketWithBackoffReconnect from '../utils/SocketWithBackoffReconnect';
import { init as initAnalytics } from '../utils/analytics';
import LoggedInTeams from '../utils/cookie/LoggedInTeams';
import { isElectron } from '../utils/desktopClient';
import { purgePersisted } from '../utils/persistRedux';
import authTokenUtil from './authTokenUtil';
import visitorAuthTokenUtil from './visitorAuthTokenUtil';

type AppThunk<ReturnType = void> = ThunkAction<
  ReturnType,
  RootState,
  unknown,
  Action
>;

const initialOrganization = {
  id: '',
  email: '',
  host: '',
  effectiveHost: '',
  name: '',
  logo: { thumb: null },
  planTier: PlanTier.Startup,
  periodEndAt: null,
  trialEndAt: null,
  isTrialEnded: false,
  isTrialEndingSoon: false,
  isLocked: false,
  billingStatus: BillingSummaryStatus.Active,
  settings: {
    authentication: AuthenticationMode.SsoAndPassword,
    seatLimit: null,
  },
  isVerified: false,
};

const initialUser = {
  id: '',
  name: '',
  avatar: {},
  title: '',
  type: UserType.User,
  email: '',
  settings: {
    notifications: {
      direct: { email: false, slack: false },
      participant: { email: false, slack: false },
      team: { email: false, slack: false },
    },
  },
  membershipGroups: [],
  identities: [],
  description: null,
};

interface SessionState {
  organization: Pick<Organization, keyof typeof initialOrganization>;
  user: Pick<CurrentSessionFragment['user'], keyof typeof initialUser>;
  socket?: Socket;
}

const initialState: SessionState = {
  organization: initialOrganization,
  user: initialUser,
  socket: undefined,
};

const sessionSlice = createSlice({
  name: 'sessionSlice',
  initialState,
  reducers: {
    sessionStart(state, action: PayloadAction<{ socket: Socket }>) {
      state.socket = action.payload.socket;
    },
    visitorSessionStart(state, action: PayloadAction<{ socket: Socket }>) {
      state.socket = action.payload.socket;
    },
    setCurrentOrganization(
      state,
      action: PayloadAction<Partial<CurrentSessionFragment['organization']>>,
    ) {
      state.organization = { ...state.organization, ...action.payload };
    },
    setCurrentUser(
      state,
      action: PayloadAction<CurrentSessionFragment['user']>,
    ) {
      state.user = { ...state.user, ...action.payload };
    },
    setUserSetting(state, action: PayloadAction<UserSettings>) {
      state.user = {
        ...state.user,
        settings: _.merge({}, state.user.settings, action.payload),
      };
    },
  },
});

const initSocket = () => {
  const socketUrl = `wss://${window.__slab.ORG_HOST}/socket`;
  const socket = new SocketWithBackoffReconnect(socketUrl, {
    timeout: 30000,
  });

  socket.onClose((event) => {
    addBreadcrumb({
      category: 'socket.close',
      message: `closed with code ${event.code}`,
      level: 'warning',
    });
  });

  socket.onError(() => {
    addBreadcrumb({
      category: 'socket.error',
      level: 'error',
    });
  });

  socket.onOpen(() => {
    addBreadcrumb({
      category: 'socket.open',
      level: 'info',
    });
  });

  socket.connect();
  return socket;
};

const clearSession = (): AppThunk => (__, getState) => {
  const { session } = getState();
  authTokenUtil.remove();

  if (session.organization.id) {
    LoggedInTeams.remove(session.organization.id);
  } else if (isElectron) {
    LoggedInTeams.removeAll();
  }

  purgePersisted();
  purgeApolloCache();
  if (session.socket) {
    session.socket.disconnect();
  }
  analytics.ready(() => {
    analytics.reset();
    window.Intercom?.('shutdown');
  });
};

function installVendors({
  organization,
  user,
  intercomHash,
}: {
  organization: CurrentSessionFragment['organization'];
  user: CurrentSessionFragment['user'];
  intercomHash: string;
}) {
  try {
    Logger.setContext(
      { id: user.id, username: user.name, email: user.email ?? undefined },
      { id: organization.id, name: organization.name, host: organization.host },
    );
  } catch (e) {
    console.error(e); // eslint-disable-line no-console
  }
  initAnalytics(organization, user, intercomHash);
}

const startSession =
  ({
    authToken,
    intercomHash,
    organization,
    user,
  }: CurrentSessionFragment): AppThunk =>
  (dispatch) => {
    authTokenUtil.set(authToken);
    const socket = initSocket();

    LoggedInTeams.add(organization.id);
    installVendors({ organization, user, intercomHash });
    dispatch(sessionSlice.actions.setCurrentOrganization(organization));
    dispatch(sessionSlice.actions.setCurrentUser(user));
    dispatch(sessionSlice.actions.sessionStart({ socket }));
    return Promise.resolve();
  };

const startVisitorSession =
  ({ authToken, user }: Pick<VisitorSession, 'authToken' | 'user'>): AppThunk =>
  (dispatch) => {
    visitorAuthTokenUtil.set(authToken);
    const socket = initSocket();

    dispatch(
      sessionSlice.actions.setCurrentUser({
        ...initialUser,
        title: '',
        insertedAt: '',
        id: user.id,
        name: user.name,
        type: UserType.Visitor,
      }),
    );

    dispatch(sessionSlice.actions.visitorSessionStart({ socket }));
  };

export const SessionActions = {
  clearSession,
  startSession,
  startVisitorSession,
  ...sessionSlice.actions,
};

export default sessionSlice.reducer;
